import { useEffect, useState } from 'react';
import {
  Breadcrumbs,
  Button,
  Container,
  CssBaseline,
  Grid,
  Paper,
  Typography,
  Table,
  TableCell,
  TableHead,
  TableBody,
  TableRow,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress
} from '@material-ui/core';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import styles from './PriceAdjustment.module.css';
import ConfirmDialog from 'components/common/ConfirmDialog/ConfirmDialog';
import Calculator from 'components/SuperAdminComponents/Calculator/Calculator';
import { useModifierApi } from './hooks/useModifierApi';
import { useQuotesApi } from './hooks/useQuotesApi';
import MinimalPrice from 'components/SuperAdminComponents/ActualPrice/ActualPrice';
import { useDispatch, useSelector } from 'react-redux';
import { setFormulas } from 'redux/slices/FormulaSlice';

const PriceAdjustment = () => {
  const [loading, setLoading] = useState();

  const dispatch = useDispatch();

  const { getModifiers, modifiers } = useModifierApi();
  const { getMinimals, postMinimal, minimals, getCommissions, postCommission, commissions } = useQuotesApi();

  const [editing, setEditing] = useState();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedModifier, setSelectedModifier] = useState(3);
  const [selectedCommission, setSelectedCommission] = useState(
    commissions?.find((commission) => commission.modifierId === 3)
  );
  const [selectedMinimal, setSelectedMinimal] = useState(minimals?.find((commission) => commission.modifierId === 3));
  const [cCalcFormula, setCCalcFormula] = useState();
  const [cICalcFormula, setCICalcFormula] = useState();
  const [mCalcFormula, setMCalcFormula] = useState();

  const cFormula = useSelector((state) => state.nonPersistedReducers?.formulaSliceReducer?.cFormula);
  const cIFormula = useSelector((state) => state.nonPersistedReducers?.formulaSliceReducer?.cIFormula);
  const mFormula = useSelector((state) => state.nonPersistedReducers?.formulaSliceReducer?.mFormula);

  const [dtcp, setDtcp] = useState({
    formulaKey: 'dtcp',
    formulaValue: 0,
    isDynamic: false
  });
  const [dtcf, setDtcf] = useState({
    formulaKey: 'dtcf',
    formulaValue: 0,
    isDynamic: false
  });
  const [print, setPrint] = useState({
    formulaKey: 'print',
    formulaValue: 0,
    isDynamic: false
  });
  const [simplePay, setSimplePay] = useState({
    formulaKey: 'simplePay',
    formulaValue: 0,
    isDynamic: false
  });
  const [userInput] = useState({
    formulaKey: 'userInput',
    isDynamic: true
  });

  const handleCopy = () => {
    dispatch(
      setFormulas({
        cFormula: cCalcFormula,
        cIFormula: cICalcFormula,
        mFormula: mCalcFormula
      })
    );
  };

  const handlePaste = () => {
    setCCalcFormula(cFormula);
    setCICalcFormula(cIFormula);
    setMCalcFormula(mFormula);
  };

  const handleChange = (panel) => (event, isExpanded) => {
    if (editing) {
      setExpanded(isExpanded ? panel : false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    console.log({
      modifierId: selectedCommission.modifierId,
      calcFormula: cCalcFormula,
      formulaParams: [print, dtcf, dtcp, simplePay, userInput],
      commissionInverse: {
        calcFormula: cICalcFormula,
        modifierId: selectedCommission.modifierId,
        formulaParams: [print, dtcf, dtcp, simplePay, userInput]
      }
    });

    await postCommission({
      modifierId: selectedCommission.modifierId,
      calcFormula: cCalcFormula,
      formulaParams: [print, dtcf, dtcp, simplePay, userInput],
      commissionInverse: {
        calcFormula: cICalcFormula,
        modifierId: selectedCommission.modifierId,
        formulaParams: [print, dtcf, dtcp, simplePay, userInput]
      }
    });
    await postMinimal({
      modifierId: selectedMinimal.modifierId,
      formulaParams: [print, dtcf, dtcp, simplePay],
      calcFormula: mCalcFormula
    });
    await getCommissions();
    await getMinimals();
    setLoading(false);
    setSelectedModifier(3);
  };

  const handleReset = () => {
    setDtcf({
      ...dtcf,
      formulaValue: selectedCommission?.formulaParams?.find((param) => param.formulaKey === 'dtcf')?.formulaValue
    });
    setDtcp({
      ...dtcp,
      formulaValue: selectedCommission?.formulaParams?.find((param) => param.formulaKey === 'dtcp')?.formulaValue
    });
    setPrint({
      ...print,
      formulaValue: selectedCommission?.formulaParams?.find((param) => param.formulaKey === 'print')?.formulaValue
    });
    setSimplePay({
      ...simplePay,
      formulaValue: selectedCommission?.formulaParams?.find((param) => param.formulaKey === 'simplePay')?.formulaValue
    });
    setCCalcFormula(selectedCommission?.calcFormula);
    setCICalcFormula(selectedCommission?.commissionInverse?.calcFormula);
    setMCalcFormula(selectedMinimal?.calcFormula);
  };

  useEffect(() => {
    getModifiers();
    getMinimals();
    getCommissions();
  }, []);

  useEffect(() => {
    setSelectedCommission(commissions?.findLast((commission) => commission.modifierId === 3));
    setSelectedMinimal(minimals?.findLast((minimal) => minimal.modifierId === 3));
  }, [commissions, minimals]);

  useEffect(() => {
    setSelectedCommission(commissions?.findLast((commission) => commission.modifierId === selectedModifier));
    setSelectedMinimal(minimals?.findLast((minimal) => minimal.modifierId === selectedModifier));
  }, [selectedModifier]);

  useEffect(() => {
    setDtcf({
      ...dtcf,
      formulaValue: selectedCommission?.formulaParams?.find((param) => param.formulaKey === 'dtcf')?.formulaValue
    });
    setDtcp({
      ...dtcp,
      formulaValue: selectedCommission?.formulaParams?.find((param) => param.formulaKey === 'dtcp')?.formulaValue
    });
    setPrint({
      ...print,
      formulaValue: selectedCommission?.formulaParams?.find((param) => param.formulaKey === 'print')?.formulaValue
    });
    setSimplePay({
      ...simplePay,
      formulaValue: selectedCommission?.formulaParams?.find((param) => param.formulaKey === 'simplePay')?.formulaValue
    });
    setCCalcFormula(selectedCommission?.calcFormula);
    setCICalcFormula(selectedCommission?.commissionInverse?.calcFormula);
    setMCalcFormula(selectedMinimal?.calcFormula);
  }, [selectedCommission, selectedMinimal]);

  return (
    <>
      <div className={styles.root}>
        <Breadcrumbs aria-label='breadcrumb' className={styles.breadcrumb}>
          <Typography color='inherit' variant='body1'>
            Árak szabályozása
          </Typography>
        </Breadcrumbs>
        <div>
          <CssBaseline />
          <main className={styles.content}>
            <Container maxWidth={'xl'} className={styles.container}>
              <Grid container spacing={3} style={{ display: 'flex', justifyContent: 'center' }}>
                <Grid item xs={12} md={8}>
                  <Paper className={styles.paper}>
                    <div className={styles.nameContainer}>
                      <Typography className={styles.title}>Ár kalkulátor</Typography>
                    </div>
                    <Select
                      labelId='demo-simple-select-label'
                      id='demo-simple-select'
                      className={styles.select}
                      value={selectedModifier}
                      label='Age'
                      onChange={(e) => {
                        setSelectedModifier(e.target.value);
                      }}>
                      {modifiers?.map((modifier) => <MenuItem value={modifier?.id}>{modifier?.name}</MenuItem>)}
                    </Select>
                    <div className={styles.rowWrapper}>
                      <div className={styles.row}>
                        <p className={styles.title}>DT jutalék (százalék):</p>
                        <TextField
                          className={styles.detail}
                          disabled={!editing}
                          InputProps={{
                            endAdornment: <InputAdornment position='start'>%</InputAdornment>
                          }}
                          onChange={(e) => {
                            setDtcp({ ...dtcp, formulaValue: e.target.value });
                          }}
                          value={dtcp?.formulaValue}
                        />
                      </div>
                      <div className={styles.row}>
                        <p className={styles.title}>SimplePay jutalék:</p>
                        <TextField
                          className={styles.detail}
                          disabled={!editing}
                          InputProps={{
                            endAdornment: <InputAdornment position='start'>%</InputAdornment>
                          }}
                          onChange={(e) => {
                            setSimplePay({
                              ...simplePay,
                              formulaValue: e.target.value
                            });
                          }}
                          value={simplePay?.formulaValue}
                        />
                      </div>
                    </div>
                    <div className={styles.rowWrapper}>
                      <div className={styles.row}>
                        <p className={styles.title}>Előállítási költség:</p>
                        <TextField
                          className={styles.detail}
                          disabled={!editing}
                          InputProps={{
                            endAdornment: <InputAdornment position='start'>Ft</InputAdornment>
                          }}
                          onChange={(e) => {
                            setPrint({
                              ...print,
                              formulaValue: e.target.value
                            });
                          }}
                          value={print?.formulaValue}
                        />
                      </div>
                      <div className={styles.row}>
                        <p className={styles.title}>DT jutalék (fix):</p>
                        <TextField
                          className={styles.detail}
                          disabled={!editing}
                          InputProps={{
                            endAdornment: <InputAdornment position='start'>Ft</InputAdornment>
                          }}
                          onChange={(e) => {
                            setDtcf({ ...dtcf, formulaValue: e.target.value });
                          }}
                          value={dtcf?.formulaValue}
                        />
                      </div>
                    </div>

                    <div className={styles.rowWrapper}>
                      <Accordion
                        style={{
                          width: '100%'
                        }}
                        disabled={!editing}
                        expanded={expanded === 'panel1'}
                        onChange={handleChange('panel1')}>
                        <AccordionSummary>
                          <div className={styles.calculatorTitleContainer}>
                            <p className={styles.title}>Jutalék számító formula ({'Fotós profit => Teljes összeg'}):</p>
                            <Typography
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: '0.5rem'
                              }}>
                              {cCalcFormula}
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 'auto'
                            }}>
                            <div className={styles.iconBorder}>
                              <EditIcon style={{ color: 'grey' }} />
                            </div>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails>
                          <div className={styles.calculatorContainer}>
                            <Calculator
                              setCalcFormula={setCCalcFormula}
                              calcFormula={cCalcFormula}
                              formulaParams={[print, dtcf, dtcp, simplePay, userInput]}
                            />
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    </div>
                    <div className={styles.rowWrapper}>
                      <Accordion
                        style={{
                          width: '100%'
                        }}
                        disabled={!editing}
                        expanded={expanded === 'panel2'}
                        onChange={handleChange('panel2')}>
                        <AccordionSummary>
                          <div className={styles.calculatorTitleContainer}>
                            <p className={styles.title}>
                              Inverz jutalék számító formula ({'Teljes összeg => Fotós profit'}):
                            </p>
                            <Typography
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: '0.5rem'
                              }}>
                              {cICalcFormula}
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 'auto'
                            }}>
                            <div className={styles.iconBorder}>
                              <EditIcon style={{ color: 'grey' }} />
                            </div>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails>
                          <div className={styles.calculatorContainer}>
                            <Calculator
                              inverse={true}
                              setCalcFormula={setCICalcFormula}
                              calcFormula={cICalcFormula}
                              formulaParams={[print, dtcf, dtcp, simplePay, userInput]}
                            />
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    </div>

                    <div className={styles.rowWrapper}>
                      <Accordion
                        className={styles.accordion}
                        disabled={!editing}
                        expanded={expanded === 'panel3'}
                        onChange={handleChange('panel3')}>
                        <AccordionSummary>
                          <div className={styles.calculatorTitleContainer}>
                            <p className={styles.title}>Minimális ár számító formula:</p>
                            <Typography
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: '0.5rem'
                              }}>
                              {mCalcFormula}
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 'auto'
                            }}>
                            <div className={styles.iconBorder}>
                              <EditIcon style={{ color: 'grey' }} />
                            </div>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails>
                          <div className={styles.calculatorContainer}>
                            <Calculator
                              setCalcFormula={setMCalcFormula}
                              calcFormula={mCalcFormula}
                              formulaParams={[print, dtcf, dtcp, simplePay]}
                            />
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    </div>
                    <div
                      className={styles.rowWrapper}
                      style={{
                        justifyContent: 'space-around',
                        marginTop: '1rem'
                      }}>
                      <Button
                        style={{
                          background: editing ? 'var(--primary-background-secondary)' : 'grey',
                          color: editing ? 'var(--primary-text-primary)' : 'lightgrey'
                        }}
                        disabled={!editing}
                        onClick={() => {
                          handleCopy();
                        }}>
                        Formulák másolása
                        <ContentCopyIcon />
                      </Button>
                      <Button
                        style={{
                          background:
                            !editing || (cFormula === '' && cIFormula === '' && mFormula === '')
                              ? 'grey'
                              : 'var(--primary-background-secondary)',
                          color: !editing ? 'lightgrey' : 'var(--primary-text-primary)'
                        }}
                        disabled={!editing || (cFormula === '' && cIFormula === '' && mFormula === '')}
                        onClick={() => {
                          handlePaste();
                        }}>
                        Formulák beillesztése
                        <ContentPasteIcon />
                      </Button>
                    </div>
                    <div className={styles.buttonsContainer}>
                      {!editing && (
                        <Button onClick={() => setEditing(true)}>
                          <EditIcon
                            style={{
                              color: 'var(--primary-background-secondary)'
                            }}
                          />{' '}
                          Szerkesztés
                        </Button>
                      )}
                      {editing && (
                        <Button
                          className={styles.saveButton}
                          onClick={() => {
                            setOpen(true);
                            setExpanded(false);
                          }}>
                          Mentés
                        </Button>
                      )}
                      {editing && (
                        <Button
                          onClick={() => {
                            handleReset();
                            setEditing(false);
                            setExpanded(false);
                          }}>
                          Mégsem
                        </Button>
                      )}
                    </div>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Paper className={styles.paper}>
                    <div className={styles.nameContainer}>
                      <Typography className={styles.title}>Minimális árak</Typography>
                      {loading && (
                        <div className={styles.loadIndicator}>
                          <Typography>Árak frissítése folyamatban</Typography>
                          <CircularProgress
                            size={20}
                            style={{
                              color: 'var(--primary-text-primary)',
                              marginLeft: '0.5rem'
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <Table>
                      <TableHead>
                        <TableCell style={{ fontWeight: 'bold' }}>Kép formátum</TableCell>
                        <TableCell style={{ fontWeight: 'bold' }}>Minimális Ár</TableCell>
                      </TableHead>
                      <TableBody>
                        {modifiers?.map((modifier, idx) => (
                          <TableRow>
                            <TableCell>{modifier?.name}</TableCell>
                            <TableCell
                              style={{
                                color: 'var(--primary-info)',
                                fontWeight: 'bold'
                              }}>
                              <MinimalPrice modifierId={modifier?.id} minimals={minimals} printer />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
            <ConfirmDialog
              open={open}
              onClose={() => setOpen(false)}
              onOk={() => {
                handleUpdate();
                setEditing(false);
                setOpen(false);
              }}
              title={'Biztosan így akarod elmenteni az adatokat?'}
              closeButtonText={'Nem'}
              functionButtonText={'Igen'}
              functionButtonColor={'var(--primary-success)'}
              multipleButtons={true}
            />
          </main>
        </div>
      </div>
    </>
  );
};

export default PriceAdjustment;
